/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { ComponentChildren, Fragment, RefObject } from 'preact';
import { useCallback, useEffect, useRef } from 'preact/compat';
import { classNames } from '../utils/UNSAFE_classNames';
import { cssProps } from '@oracle/oraclejet-cssprops';
import {
  focusOn,
  focusOnEnd,
  focusOnStart,
  focusWithin,
  getActiveElement,
  isTabbableElement
} from '../utils/PRIVATE_tabbableUtils';
import FocusGuard from './FocusGuard';

type Props = {
  children: ComponentChildren;
  /**
   * Optional Ref to an element to focus on when component mounts
   * @default - undefined - behavior is to focus on first tabbableElement
   */
  autoFocusRef?: RefObject<HTMLElement | null>;

  /**
   * Sets whether focus is managed and whether guards are present
   * @default false
   */
  isDisabled?: boolean;

  /**
   * Why a isDisabled prop as compared to ONLY supporting conditional rendering?
   *
   *   1) We need an actual HTML Element for the trap, conditional rendering an element potentially breaks consumer CSS
   *   2) Conditional rendering can short-circuit event bubbling in preact and lead to expected events not bubbling.
   */

  /**
   * Optional Ref to element to focus on when component un-mounts, false to disable.
   * @default - undefined - behavior is to focus on the original activeElement when focus trap was enabled
   */
  restoreFocusRef?: RefObject<HTMLElement> | false;
};

/**
 * Trap focus within a DOM node.  When a user hits Tab or Shift+Tab, focus remains in the focus trap.
 */
export const FocusTrap = ({
  autoFocusRef,
  children,
  isDisabled = false,
  restoreFocusRef
}: Props) => {
  const trapRef = useRef<HTMLDivElement>(null);
  const startGuardRef = useRef<HTMLDivElement>(null);
  const endGuardRef = useRef<HTMLDivElement>(null);
  const lastActiveElementRef = useRef<HTMLElement | null>(null);

  // classes to be set on the children wrapper
  const classes = classNames([childrenStyles]);

  /**
   * contain focus if trap is enabled, and target is not already contained
   *
   * calling cases for side-effect of setting focus
   *   - on initial mount of (enabled) trap, where target is not inside trap
   *   - on enabling of a disabled trap, where target is not inside trap
   *   - target of click event is outside of trap.
   */
  const conditionallyContainFocus = useCallback(
    (target: HTMLElement | null) => {
      const trap = trapRef.current;
      //  short-circuit, if trap or target not valid
      if (isDisabled || !trap || !target) return;

      // just update ref if, target already has focus
      if (target && hasFocus([trap, startGuardRef.current, endGuardRef.current], target)) {
        lastActiveElementRef.current = target;
        return;
      }

      // set focus to last active tab-stop if lastElement is defined and inside trap
      if (lastActiveElementRef.current) {
        focusOn(lastActiveElementRef.current);
      } else {
        // otherwise just contain focus within trap as target is outside enabled trap and we have no last-known tab-stop
        focusWithin(trap);
      }
    },
    [isDisabled]
  );

  // loop focus
  const handleStartGuardOnFocus = (event: FocusEvent) => {
    if (isDisabled || !trapRef.current) return;
    focusOnEnd(trapRef.current);

    // prevent scroll jump and bubbling this event up as a focus event
    event.stopPropagation();
    event.preventDefault();
  };

  const handleEndGuardOnFocus = (event: FocusEvent) => {
    if (isDisabled || !trapRef.current) return;
    focusOnStart(trapRef.current);

    // prevent scroll jump and bubbling guard event up as a focus event
    event.stopPropagation();
    event.preventDefault();
  };

  const handleTrapOnFocus = (event: FocusEvent) => {
    lastActiveElementRef.current = event.target as HTMLElement;
  };

  // handle initial render or re-render as enabled
  useEffect(() => {
    const trap = trapRef.current;
    if (!trap || isDisabled) return;

    const originalActiveElement = getActiveElement(trap);

    // init focus
    if (autoFocusRef && autoFocusRef.current) {
      focusOn(autoFocusRef.current);
    } else {
      conditionallyContainFocus(originalActiveElement);
    }

    const restoreFocusRefForCleanup = restoreFocusRef;

    return () => {
      if (restoreFocusRefForCleanup === false) return;
      restoreFocusAfterUnmount(restoreFocusRefForCleanup?.current || originalActiveElement);
    };
  }, [autoFocusRef, conditionallyContainFocus, isDisabled, restoreFocusRef]);

  return (
    <Fragment>
      {!isDisabled && <FocusGuard onFocus={handleStartGuardOnFocus} ref={startGuardRef} />}
      <div onFocus={handleTrapOnFocus} ref={trapRef} class={classes}>
        {children}
      </div>
      {!isDisabled && <FocusGuard onFocus={handleEndGuardOnFocus} ref={endGuardRef} />}
    </Fragment>
  );
};

// setTimeout ensures that unmount lifecycle has completed
const restoreFocusAfterUnmount = (element: HTMLElement | null) => {
  if (element && isTabbableElement(element)) {
    setTimeout(() => {
      focusOn(element);
    }, 0);
  }
};

// is focus already contained?
const hasFocus = (elements: (HTMLElement | null)[], target: Element) => {
  if (elements.length === 0) return false;
  const activeElement = target ?? getActiveElement(elements[0] as Node);
  return elements.some((el) => el === activeElement || el?.contains(activeElement));
};

// remove margins from children
const childrenStyles = cssProps`${{
  /* @ts-ignore */
  '& > *': {
    marginTop: '0 !important',
    marginBottom: '0 !important',
    marginLeft: '0 !important',
    marginRight: '0 !important'
  }
}}`;
